import React, {useMemo} from 'react';

import {SortingDirections, ValueFilterHeader} from 'src/components/filter-header';
import {FilterPredicate} from 'src/hooks/filtered-data/filter-predicate';
import {useFilteredData} from 'src/hooks/filtered-data/useFilteredData';
import {SortConfig} from 'src/hooks/useSortingRows';

type Props<T, F, C extends keyof F, S> = {
    rows: T[],
    filters: F,
    setFilter: (filterType: string, filterValue: string[]) => void,
    filterPredicates: (filters: F) => FilterPredicate<T>[],
    filteringColumnType: C,
    sortingColumnType?: S,
    onSort?: (config: SortConfig<S>) => void,
    sortConfig?: SortConfig<S>,
    columnDataHandler: (filteredRows: T[], columnType: C) => string[],
    caption: string,
};

export const ColumnHeader = <T, F, C extends keyof F, S>(props: Props<T, F, C, S>) => {
    const {
        filters,
        setFilter,
        filterPredicates,
        rows,
        onSort,
        sortConfig,
        filteringColumnType,
        sortingColumnType,
        columnDataHandler,
        caption,
    } = props;

    const filtersExcludingCurrentColumn = {...filters, [filteringColumnType]: []};
    const filteredRows = useFilteredData(rows, filtersExcludingCurrentColumn, filterPredicates);

    const columnData = useMemo(() => {
        return columnDataHandler(filteredRows, filteringColumnType);
    }, [filteredRows, filteringColumnType, columnDataHandler]);

    const filterHandler = (filterValues: string[]) => {
        setFilter(filteringColumnType as string, filterValues);
    };

    const sortHandler = (direction: SortingDirections) => {
        if (onSort) {
            onSort({key: sortingColumnType, direction});
        }
    };

    return (
        <ValueFilterHeader
            caption={caption}
            columnData={columnData}
            setFilter={filterHandler}
            filters={filters[filteringColumnType] as unknown as string[]}
            isSorted={!!sortConfig && sortConfig.key === sortingColumnType}
            onSort={onSort ? sortHandler : undefined}
        />
    );
};
